/**
 * 66.7 (a), IDEAS X32 (the owner: snap, step away to leave): the player's take-cover, host side. Shift snaps to the
 * nearest cover point in reach (F1's cover map, core/coverMap.ts) — the body walks there, as fast as a walk — and holds it;
 * right mouse held peeks out over or round it; any step away leaves. Holding is the unit's intent (UnitIntent.cover, the
 * one field the bots at their posts set too), so the Match keeps it on the body (PlayerState.cover) and everyone draws it.
 *
 * Pure: which point, where the body stands at it (pressed to the prop), the hold and stance at it against where you look,
 * and the walk to it. The host owns the state.
 *
 * The owner, playing the first cut (Oct 11): "the commands aren't great, need to get rid of the 'shift to cover' thing in
 * the middle as it's annoying and messing up aim and sensitivity … the poses themselves behind the cover are bad, user just
 * stands sort of behind it and doesn't lean into it at all." So the pose is chosen once, as you take the cover, and the
 * side you lean to only changes when your look swings well past the cover's facing (`TAKE_COVER.sideSwitch`) — the stance
 * and the body no longer change under the mouse as you aim round; and the body stands against the prop (the walk's own
 * contact), not at the cover point a body and a half off it. How the drawn body leans into the cover is lane E's.
 * The lean is the peek's side step, as the bots' is (lane C's 66.7 lean): out round an edge the body steps to the point's
 * peek spot, so the shot clears; over low cover it stands up where it is. Not yet (lean step 2): a pose-only lean that
 * moves the eye, the muzzle and the hit volume without the step.
 */
import type { CoverHides, CoverHold, CoverPoint, CoverSide, CoverSlot, Dig, Stance, Vec3 } from '../types';
import { digCover } from '../core/dig';
import { coverBearing } from '../core/coverMap';
import { datan2, dcos, dsin } from '../core/dmath';
import { angleBetween } from '../core/surfaceNav';
import { projectToTangent, rightOf } from '../core/surfaceWalk';
import { vecAdd, vecCross, vecDot, vecNormalize, vecScale } from '../math';
import { coverPoseFor, COVER_STANCE } from '../rendering/personModel';

/** The dials (people: × the scale's person height). */
export const TAKE_COVER = {
  /** How far Shift reaches for a cover point (a few steps, not a run across the field). */
  reachPeople: 3,
  /** At the spot when this close (the walk stops and the hold begins) — or when the walk stalls near it (pressed to the prop). */
  arrivePeople: 0.03,
  /** Within this of the spot the walk slows in proportion (a frame's full step would carry it past a spot this close). */
  slowPeople: 0.25,
  /** A walk that gains less than `stallPeople` in `stallSeconds` within `nearPeople` of its spot has arrived (the prop stops it). */
  stallPeople: 0.02,
  stallSeconds: 0.15,
  nearPeople: 0.5,
  /** The look's threat: a point this far along where you face (COVER_POINTS.threatPeople's distance). */
  threatPeople: 12,
  /** A walk to the point that takes longer than this has stuck (a slope, a prop in the way): the snap is dropped. */
  walkSeconds: 3,
  /** The side leaned round switches only when the look falls this far (radians) to the other side of the cover's facing. */
  sideSwitch: 0.6,
  /** The body pressed to its prop: the walk's contact (the prop's foot and the body's half width) and this many body radii. */
  pressBodies: 0.05,
} as const;

/** The planet-centred direction `distance` along the ground from `up` toward `heading` (what coverBearing takes). */
export function aheadOf(up: Vec3, heading: Vec3, distance: number, radius: number): Vec3 {
  const n = vecNormalize(up), t = projectToTangent(heading, n), th = distance / radius;
  return vecNormalize(vecAdd(vecScale(n, dcos(th)), vecScale(t, dsin(th))));
}

/**
 * The cover Shift takes: of the hard cover points (concealment stops no bullet) within `reach` of `at`, the nearest that
 * hides something from `toward` (where you look); none does, the nearest at all (you turn to face it from there). Null:
 * no cover in reach.
 */
export function pickCover(points: readonly CoverPoint[], at: Vec3, toward: Vec3, reach: number, radius: number): CoverPoint | null {
  const a = vecNormalize(at);
  let best: CoverPoint | null = null, bestD = Infinity, bestHides = false;
  for (const p of points) {
    if (p.concealment) continue;
    const d = angleBetween(a, vecNormalize(p.dir)) * radius;
    if (d > reach) continue;
    const hides = p.shelter[coverBearing(p, toward)] !== null;
    if ((hides && !bestHides) || (hides === bestHides && d < bestD)) { best = p; bestD = d; bestHides = hides; }
  }
  return best;
}

/**
 * Where the body stands at `p` against its prop (`prop`: the walk's obstacle, its foot `dir` and `radius`): the point drawn
 * in toward the prop until the body would touch it (the walk stops a body there: radius + `unitRadius`) and `margin` more.
 * A cover point stands a body and a half off its prop (F1's ring, clear of the bots' steering); a person taking cover puts
 * a shoulder or the chest to it. Not past the point (a point already at the contact stays).
 */
export function pressedSpot(p: Pick<CoverPoint, 'dir'>, prop: { dir: Vec3; radius: number }, unitRadius: number, margin: number, radius: number): Vec3 {
  const n = vecNormalize(p.dir), d = angleBetween(n, vecNormalize(prop.dir)) * radius;
  const by = d - (prop.radius + unitRadius + margin);
  if (by <= 0) return p.dir;
  const t = projectToTangent(prop.dir, n), th = by / radius;
  return vecNormalize(vecAdd(vecScale(n, dcos(th)), vecScale(t, dsin(th))));
}

/**
 * How to hold `p` against `toward`, out of it (`out`: peeking) or down behind it: lane E's pose and the side leaned round
 * (personModel coverPoseFor), the side for the HUD and the camera, and the stance to send — the pose's own (low and edge
 * crouched, corner standing, crest prone), standing up out of low cover to fire over it — and where the body stands for
 * it (`spot`): down, the point; out, the point's peek that side (a step round the edge; over, the point itself).
 *
 * What it hides from where you look picks the pose: a body standing — round an edge (the side your look falls to of the
 * cover's facing, where the point has a peek that way); crouched — over it, if it has a peek over, else round an edge;
 * lying — over its crest. Looking where it hides nothing, its own facing's answer (you hold it the way it faces).
 *
 * `kept` (the hold so far, after the take): what it hides stays as taken (so the pose and stance never change under the
 * mouse), and an edge's side switches only when the look is more than `TAKE_COVER.sideSwitch` to the other side of the
 * cover's facing. `at`: where the body stands down (the pressed spot; default the point). `lean`: a lean key held (Q / E)
 * — that side, whatever the look (over low cover, it rises).
 */
export function coverHoldFor(p: Pick<CoverPoint, 'dir' | 'facing' | 'shelter' | 'peeks'>, toward: Vec3, out: boolean, kept?: { hides: NonNullable<CoverHides>; side: CoverSide }, at: Vec3 = p.dir, lean?: 'left' | 'right'): { hold: CoverHold; side: CoverSide; hides: NonNullable<CoverHides>; stance: Stance; spot: Vec3 } {
  const hides = kept?.hides ?? p.shelter[coverBearing(p, toward)] ?? p.shelter[0] ?? 'crouch';
  const sides = new Set(p.peeks.map(k => k.side));
  const n = vecNormalize(p.dir), t = projectToTangent(toward, n);
  const look = datan2(vecDot(t, vecCross(p.facing, n)), vecDot(t, p.facing));   // + to the right of the facing (the shelter's bearings turn to facing × up)
  const pick = (right: boolean): CoverSide => {
    const want: CoverSide = right ? 'right' : 'left', other: CoverSide = right ? 'left' : 'right';
    return sides.has(want) || !sides.has(other) ? want : other;
  };
  const over = hides === 'prone' || (hides === 'crouch' && sides.has('over')) || (sides.size === 1 && sides.has('over'));
  const side: CoverSide = over ? 'over'
    : lean ? lean
    : !kept || kept.side === 'over' ? pick(look >= 0)
    : kept.side === 'left' && look > TAKE_COVER.sideSwitch ? pick(true)
    : kept.side === 'right' && look < -TAKE_COVER.sideSwitch ? pick(false)
    : kept.side;
  const pose = coverPoseFor(hides, side) ?? { pose: 'low' as const };
  const stance: Stance = out && pose.pose === 'low' ? 'stand' : COVER_STANCE[pose.pose];
  const spot = out ? p.peeks.find(k => k.side === side)?.dir ?? at : at;
  return { hold: { pose: pose.pose, ...(pose.side !== undefined ? { side: pose.side } : {}), out }, side, hides, stance, spot };
}

/** How far (person heights) a lean may move along the cover from the pressed spot to clear it, smallest first (0: in place). */
export const LEAN_SHIFTS_PEOPLE = [0, 0.1, 0.2, 0.3, 0.45, 0.6, 1] as const;

/**
 * Where to stand to lean out round the cover on `side` and fire at what you aim at: from the pressed spot `at`, the
 * smallest shift along the cover that way (`facing` × up is a body's right facing it) whose leaned gun clears — lane C's
 * leanSide for the bots' posts, from the body pressed to the prop rather than from the ring. `clears(spot)`: the leaned
 * muzzle there sees the aim; `free(spot)`: the walk can stand there. Null: none clears (the lean stays in place; the
 * crosshair's *blocked* says so).
 */
export function leanSpot(at: Vec3, facing: Vec3, side: 'left' | 'right', person: number, radius: number, clears: (spot: Vec3) => boolean, free: (spot: Vec3) => boolean): Vec3 | null {
  const n = vecNormalize(at), right = vecNormalize(vecCross(facing, n)), s = side === 'right' ? 1 : -1;
  for (const shift of LEAN_SHIFTS_PEOPLE) {
    const th = (shift * person) / radius;
    const q = shift === 0 ? at : vecNormalize(vecAdd(vecScale(n, dcos(th)), vecScale(right, s * dsin(th))));
    if (shift > 0 && !free(q)) continue;
    if (clears(q)) return q;
  }
  return null;
}

/** A prop as the face reads it: the walk's obstacle (its foot, radius, height, and a rock's drawn profile and sink). */
export interface FaceProp { dir: Vec3; radius: number; height: number; profile?: readonly number[]; sink?: number }

/** A prop's drawn half-width at `h` over the ground (a rock by its profile, × its radius; else its radius). */
export function halfWidthAt(o: FaceProp, h: number): number {
  if (!o.profile?.length || !(o.height > 0)) return o.radius;
  const k = o.profile.length, x = Math.max(0, Math.min(k - 1, (h / o.height + (o.sink ?? 0)) * (k - 1)));
  const i = Math.min(k - 2, Math.floor(x)), f = x - i;
  return o.radius * (o.profile[i] * (1 - f) + o.profile[i + 1] * f);
}

/**
 * 66.8: the dig a holder at `at` stands in and its face for lane E's dug-in poses (person units — the pose's own, as digCover
 * gives them): a dig half dug or more whose centre is within half a person (`halfPerson`, world) of it, the sim's own rule
 * for a holder taking a dig's pose (ai/actionPositions.ts postPose); its height (the lip's top over the floor) and the dug
 * face's gap at the chest. Null: in no dig. `radius` the planet's. Pure.
 */
export function digFace(digs: readonly Dig[] | undefined, at: Vec3, halfPerson: number, radius: number): { dig: Dig; gap: number; height: number } | null {
  if (!digs?.length) return null;
  const a = vecNormalize(at);
  for (const g of digs) {
    if (g.done < 0.5 || angleBetween(vecNormalize(g.dir), a) * radius > halfPerson) continue;
    const c = digCover(g.kind, g.material, g.done);
    if (c) return { dig: g, gap: c.gap, height: c.height };
  }
  return null;
}

/**
 * 66.7 (a)'s rework: the face of the cover a unit at `at` stands against, for lane E's pressed poses (world units): of the
 * props whose walk contact it is within `near` of, the nearest — `gap` from the spot to its drawn face toward its foot at
 * `chest` over the ground, `height` its drawn top over the ground, `toward` the unit tangent at the spot toward its foot
 * (67.60's cover aim: the pose faces it whatever the look). Null: none (the ground's cover, or none at hand).
 */
export function coverFace(at: Vec3, props: readonly FaceProp[], chest: number, unitRadius: number, near: number, radius: number): { gap: number; height: number; toward: Vec3 } | null {
  const n = vecNormalize(at);
  let best: FaceProp | null = null, bestD = Infinity, bestGap = Infinity;
  for (const o of props) {
    const d = gapTo(n, o.dir, radius), over = d - o.radius - unitRadius;
    if (over > near || d >= bestD) continue;
    best = o; bestD = d; bestGap = over;
  }
  if (!best || !(bestGap <= near)) return null;
  return { gap: Math.max(0, bestD - halfWidthAt(best, chest)), height: best.height * (1 - (best.sink ?? 0)), toward: vecNormalize(projectToTangent(vecNormalize(best.dir), n)) };
}

/** How far along the ground `target` is from `up` (world units on a planet of `radius`). */
export function gapTo(up: Vec3, target: Vec3, radius: number): number {
  return angleBetween(vecNormalize(up), vecNormalize(target)) * radius;
}

/**
 * The walk to `target` from `up` facing `heading`: forward / right in the body's frame (the intent's move), or null when
 * within `arrive` (there). Within `slow` of it, slower in proportion to what is left — a full step a frame carried the body
 * past a spot a tenth of a person away and back, frame after frame.
 */
export function walkTo(up: Vec3, heading: Vec3, target: Vec3, arrive: number, radius: number, slow = 0): { forward: number; right: number } | null {
  const n = vecNormalize(up), goal = vecNormalize(target), gap = gapTo(n, goal, radius);
  if (gap <= arrive) return null;
  const m = projectToTangent(goal, n), h = projectToTangent(heading, n), k = slow > 0 ? Math.min(1, gap / slow) : 1;
  return { forward: vecDot(m, h) * k, right: vecDot(m, rightOf(h, n)) * k };
}

// ---------------------------------------------------------------------------
// 66.9 (the owner: "for the square stacked rocks, the oblate stacked rocks, the big rectangular slabs (when > 1.5 player
// width), the leans either don't reach around enough to stick out, or the character walks all the way around the edge of
// the object and is fully exposed … maybe some concept of cover slots on coverable objects and you snap into a slot that
// has the right distances already sorted out"): lane F's slots (core/coverSlots.ts) — at each end of a wide face a slot a
// lean's reach inside the edge, its lean side known; along the face a middle slot (a rise over it when it is low enough)
// ---------------------------------------------------------------------------

/** The bonus (person heights) an end slot whose lean is the look's side has over a nearer slot: you take the edge you'd fire round. */
export const SLOT_LEAN_BONUS_PEOPLE = 1;

/**
 * The slot Shift takes: of `slots` within `reach` of `at`, one that hides something from `toward` (where you look), the
 * nearest — an end slot whose lean is the side your look falls to counted `bonus` nearer. Null: none (the cover points, as
 * before: a trunk, a narrow rock).
 */
export function pickSlot(slots: readonly CoverSlot[], at: Vec3, toward: Vec3, reach: number, radius: number, bonus = 0): CoverSlot | null {
  let best: CoverSlot | null = null, bestScore = Infinity;
  for (const s of slots) {
    const d = gapTo(at, s.dir, radius);
    if (d > reach || s.shelter[coverBearing(s, toward)] === null) continue;
    const look = vecDot(projectToTangent(toward, vecNormalize(s.dir)), s.along) >= 0 ? 1 : -1;
    const score = d - (s.kind === 'end' && s.lean === look ? bonus : 0);
    if (score < bestScore) { best = s; bestScore = score; }
  }
  return best;
}

/**
 * How to hold a slot, out of it (`out`) or down behind it: an end slot leans round its own edge (its `lean`: CoverHold.side,
 * fixed — no switching under the mouse), kneeling where the rock hides a crouched body and standing where it hides a
 * standing one, in place at its `dir` (the slot stands where the lean clears); a middle slot is low cover, rising to fire
 * when it can be fired over (`peekOver`), else down only (the lean keys step to the face's ends).
 */
export function slotHoldFor(slot: Pick<CoverSlot, 'kind' | 'lean' | 'peekOver' | 'shelter' | 'dir'>, out: boolean): { hold: CoverHold; side: CoverSide; stance: Stance; spot: Vec3 } {
  const hides = slot.shelter[0] ?? 'crouch';
  if (slot.kind === 'end' && slot.lean) {
    const side: CoverSide = slot.lean === 1 ? 'right' : 'left';
    const pose = coverPoseFor(hides === 'prone' ? 'crouch' : hides, side) ?? { pose: 'edge' as const, side: slot.lean };
    return { hold: { pose: pose.pose, ...(pose.side !== undefined ? { side: pose.side } : {}), out }, side, stance: COVER_STANCE[pose.pose], spot: slot.dir };
  }
  const rise = out && slot.peekOver;
  return { hold: { pose: 'low', out: rise }, side: 'over', stance: rise ? 'stand' : 'crouch', spot: slot.dir };
}

/** The face's slot a lean key steps to (66.9): its end on that side (+1 the slot's `along`), or null — none, or already there. */
export function slotFor(slots: readonly CoverSlot[], from: Pick<CoverSlot, 'key' | 'kind' | 'lean'>, lean: 1 | -1): CoverSlot | null {
  if (from.kind === 'end' && from.lean === lean) return null;
  const face = from.key.slice(0, from.key.lastIndexOf(':'));
  return slots.find(s => s.kind === 'end' && s.lean === lean && s.key.startsWith(face + ':')) ?? null;
}
